// src/services/azurePendenzenMarkupsService.js
// Azure Table Storage Service für räumliche Markierungen (Punkt/Linie/Fläche) auf
// Plänen, die einer Pendenz zugeordnet sind. Muster: azurePendenzenService.js.
// US-PLAN-003: Punktmarkierung setzen/anzeigen/löschen, Verknüpfung zu Pendenz.
// US-PLAN-004: Linien-/Flächenmarkierung (gleiche Struktur, anderer Typ/Geometrie).
// US-PLAN-006 (später): Markierungen bei neuer Planversion fortschreiben (positionPruefen).
const CONFIG = {
  storageAccount: "mfprojects",
  tableName: "pendenzenMarkups",
  // Gleicher Account-SAS-Token wie die übrigen projektbezogenen Tabellen.
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const getBaseUrl = () =>
  `https://${CONFIG.storageAccount}.table.core.windows.net/${CONFIG.tableName}`;

const generateMarkupId = () =>
  `markup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const cleanValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

let tableEnsured = false;
const ensureTableExists = async () => {
  if (tableEnsured) return true;
  try {
    const url = `https://${CONFIG.storageAccount}.table.core.windows.net/Tables?${CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify({ TableName: CONFIG.tableName }),
    });
    if (response.ok || response.status === 409) {
      tableEnsured = true;
      return true;
    }
    return false;
  } catch (error) {
    console.error("Markierungen: Tabelle konnte nicht angelegt werden:", error);
    return false;
  }
};

const mapEntity = (item) => ({
  id: item.RowKey,
  pendenzId: item.PartitionKey,
  projectUuid: item.ProjectUuid || "",
  planId: item.PlanId || "",
  planVersionId: item.PlanVersionId || "",
  seite: parseInt(item.Seite, 10) || 1,
  typ: item.Typ || "point",
  geometrie: (() => {
    try {
      return item.Geometrie ? JSON.parse(item.Geometrie) : null;
    } catch {
      return null;
    }
  })(),
  beschriftung: item.Beschriftung || "",
  positionPruefen: item.PositionPruefen === true || item.PositionPruefen === "true",
  erstelltAm: item.ErstelltAm || "",
  erstelltVon: item.ErstelltVon || "",
});

const toEntity = (pendenzId, rowKey, data) => ({
  PartitionKey: pendenzId,
  RowKey: rowKey,
  ProjectUuid: cleanValue(data.projectUuid),
  PlanId: cleanValue(data.planId),
  PlanVersionId: cleanValue(data.planVersionId),
  Seite: data.seite != null ? parseInt(data.seite, 10) : 1,
  Typ: cleanValue(data.typ || "point"),
  Geometrie: JSON.stringify(data.geometrie || null),
  Beschriftung: cleanValue(data.beschriftung),
  PositionPruefen: data.positionPruefen === true,
  ErstelltAm: cleanValue(data.erstelltAm),
  ErstelltVon: cleanValue(data.erstelltVon),
});

const genericScan = async (filter) => {
  await ensureTableExists();
  let allEntries = [];
  let nextPartitionKey = null;
  let nextRowKey = null;
  do {
    let url = `${getBaseUrl()}()?${CONFIG.sasToken}`;
    if (filter) url += `&$filter=${encodeURIComponent(filter)}`;
    if (nextPartitionKey && nextRowKey) {
      url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
      url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
    }
    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error:", errorText);
      throw new Error(`Azure Table Error: ${response.status}`);
    }
    const data = await response.json();
    allEntries = allEntries.concat(data.value || []);
    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey && nextRowKey);
  return allEntries;
};

// Alle Markierungen eines Projekts (für Zähler/Icon je Pendenz in der Tabelle).
export const getMarkupsForProject = async (projectUuid) => {
  if (!projectUuid) return [];
  try {
    const entries = await genericScan(`ProjectUuid eq '${projectUuid}'`);
    return entries.map(mapEntity);
  } catch (error) {
    console.error("Fehler beim Laden der Markierungen:", error);
    throw new Error("Die Markierungen konnten nicht geladen werden.");
  }
};

// Markierungen genau einer Pendenz (schneller Partition-Filter).
export const getMarkupsForPendenz = async (pendenzId) => {
  if (!pendenzId) return [];
  try {
    await ensureTableExists();
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;
    do {
      let url = `${getBaseUrl()}()?${CONFIG.sasToken}&$filter=${encodeURIComponent(
        `PartitionKey eq '${pendenzId}'`
      )}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }
      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });
      if (!response.ok) throw new Error(`Azure Table Error: ${response.status}`);
      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);
      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);
    return allEntries.map(mapEntity);
  } catch (error) {
    console.error("Fehler beim Laden der Markierungen:", error);
    throw new Error("Die Markierungen konnten nicht geladen werden.");
  }
};

// Alle Markierungen einer bestimmten Planversion + Seite (unabhängig davon, zu
// welcher Pendenz sie gehören) – für die Anzeige im Viewer.
export const getMarkupsForPlanVersion = async (planVersionId, seite) => {
  if (!planVersionId) return [];
  try {
    const seiteNum = parseInt(seite, 10);
    const filter =
      seite != null && Number.isFinite(seiteNum)
        ? `PlanVersionId eq '${planVersionId}' and Seite eq ${seiteNum}`
        : `PlanVersionId eq '${planVersionId}'`;
    const entries = await genericScan(filter);
    return entries.map(mapEntity);
  } catch (error) {
    console.error("Fehler beim Laden der Markierungen:", error);
    throw new Error("Die Markierungen konnten nicht geladen werden.");
  }
};

// Neue Markierung anlegen.
export const createMarkup = async (pendenzId, data) => {
  try {
    await ensureTableExists();
    const rowKey = generateMarkupId();
    const now = new Date().toISOString();
    const entity = toEntity(pendenzId, rowKey, { ...data, erstelltAm: now });
    const response = await fetch(`${getBaseUrl()}?${CONFIG.sasToken}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Save Error Details:", errorText);
      throw new Error(`Azure Save Error: ${response.status}`);
    }
    return mapEntity(entity);
  } catch (error) {
    console.error("Fehler beim Anlegen der Markierung:", error);
    throw new Error("Die Markierung konnte nicht gespeichert werden.");
  }
};

// Markierung aktualisieren (Geometrie verschieben, positionPruefen zurücksetzen,
// bei US-PLAN-006 auch planVersionId fortschreiben).
export const updateMarkup = async (pendenzId, markupId, data) => {
  try {
    const entity = toEntity(pendenzId, markupId, data);
    const url = `${getBaseUrl()}(PartitionKey='${pendenzId}',RowKey='${markupId}')?${CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Update Error Details:", errorText);
      throw new Error(`Azure Update Error: ${response.status}`);
    }
    return mapEntity(entity);
  } catch (error) {
    console.error("Fehler beim Aktualisieren der Markierung:", error);
    throw new Error("Die Markierung konnte nicht aktualisiert werden.");
  }
};

// Markierung löschen.
export const deleteMarkup = async (pendenzId, markupId) => {
  try {
    const url = `${getBaseUrl()}(PartitionKey='${pendenzId}',RowKey='${markupId}')?${CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure Delete Error: ${response.status}`);
    }
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen der Markierung:", error);
    throw new Error("Die Markierung konnte nicht gelöscht werden.");
  }
};

// US-PLAN-006: alle Markierungen einer alten Planversion auf die neue Version
// fortschreiben (planVersionId aktualisieren, positionPruefen = true setzen).
export const carryOverMarkupsToNewVersion = async (oldVersionId, newVersionId) => {
  try {
    const markups = await getMarkupsForPlanVersion(oldVersionId);
    const updated = [];
    for (const markup of markups) {
      const result = await updateMarkup(markup.pendenzId, markup.id, {
        ...markup,
        planVersionId: newVersionId,
        positionPruefen: true,
      });
      updated.push(result);
    }
    return updated;
  } catch (error) {
    console.error("Fehler beim Fortschreiben der Markierungen:", error);
    throw new Error("Die Markierungen konnten nicht fortgeschrieben werden.");
  }
};
